import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useEffect } from 'react';
import { useOnClickOutside } from '@ln/hooks';
import { Button as Button$1 } from '@ln/common-ui-button';
import { Text } from '@ln/common-ui-text';
import { Skeleton } from '@ln/common-ui-skeleton';
import classNames from 'classnames';
import { Icon } from '@ln/common-ui-icon';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

const Card = ({ btnText, className, description, href, isLoading, onClick, target, time, title, type, wasRead, }) => {
    const classes = classNames('notification-card', className, {
        [`${type}`]: type,
        'read': wasRead,
    });
    const Wrapper = href ? Button$1 : 'article';
    if (isLoading)
        return jsxs("article", Object.assign({ className: 'notification-card loading' }, { children: [jsxs("div", Object.assign({ className: "notification-card-header" }, { children: [jsx(Skeleton, { fill: true, height: 16 }), jsx(Skeleton, { fill: true, height: 16 })] })), jsx(Skeleton, { width: 46, height: 16 })] }));
    return (jsxs(Wrapper, Object.assign({ className: classes, 
        // eslint-disable-next-line no-extra-boolean-cast
        title: !!btnText ? `Ir a ${btnText}` : title }, (href && { href, target }), (onClick && { onClick }), { children: [jsxs("div", Object.assign({ className: "notification-card-header" }, { children: [jsx(Text, Object.assign({ as: "h4", className: "notification-card-title" }, { children: title })), jsx(Text, Object.assign({ as: "p", className: "notification-card-description" }, { children: description }))] })), !!btnText && jsx("div", Object.assign({ className: "notification-card-btn" }, { children: btnText })), jsx(Text, Object.assign({ as: "p", className: "notification-card-time" }, { children: time }))] })));
};

const toggleDrawer = ({ id, show }) => {
    if (typeof document === 'undefined')
        return;
    const drawer = document.querySelector(`[data-id=${id}]`);
    const overlay = document.querySelector(`[data-id=overlay-${id}]`);
    if (!drawer)
        return;
    if (show) {
        drawer.dataset['visible'] = 'true';
        overlay === null || overlay === void 0 ? void 0 : overlay.classList.remove("hidden");
        document.body.classList.add('overflow-hidden');
    }
    else {
        drawer.dataset['visible'] = 'false';
        overlay === null || overlay === void 0 ? void 0 : overlay.classList.add("hidden");
        document.body.classList.remove('overflow-hidden');
    }
};

const Header = (_a) => {
    var { className, children, handleClose, sticky, drawerId } = _a, r = __rest(_a, ["className", "children", "handleClose", "sticky", "drawerId"]);
    const classes = classNames('notification-drawer-header', className, {
        'sticky': sticky
    });
    const closeDrawer = () => {
        toggleDrawer({ id: drawerId, show: false });
        handleClose === null || handleClose === void 0 ? void 0 : handleClose();
    };
    return (jsxs("div", Object.assign({ className: classes }, r, { children: [children, jsx(Button$1, Object.assign({ title: "Cerrar men\u00FA", className: "notification-drawer-close", onClick: closeDrawer }, { children: jsx("svg", Object.assign({ xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 24 24", width: 24, height: 24 }, { children: jsx("path", { fill: "#333333", fillRule: "evenodd", d: "M12 10.586l4.243-4.243a1 1 0 111.414 1.414L13.414 12l4.243 4.243a1 1 0 11-1.414 1.414L12 13.414l-4.243 4.243a1 1 0 11-1.414-1.414L10.586 12 6.343 7.757a1 1 0 111.414-1.414L12 10.586z", clipRule: "evenodd" }) })) }))] })));
};

const Body = (_a) => {
    var { className, children } = _a, r = __rest(_a, ["className", "children"]);
    const classes = classNames('notification-drawer-body', className);
    return (jsx("div", Object.assign({ className: classes }, r, { children: children })));
};

const Tooltip = (_a) => {
    var { children, className, visible = false, 
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    closeTooltip = () => { }, closable, autoClose, position = 'bottom' } = _a, r = __rest(_a, ["children", "className", "visible", "closeTooltip", "closable", "autoClose", "position"]);
    const _className = classNames('notification-drawer-tooltip', className);
    const tooltipRef = useRef(null);
    useOnClickOutside(tooltipRef, closeTooltip);
    useEffect(() => {
        if (!autoClose || !visible)
            return;
        const timer = setTimeout(() => {
            closeTooltip();
        }, autoClose);
        return () => clearTimeout(timer);
    }, [autoClose, visible]);
    return (jsxs("div", Object.assign({ className: _className, ref: tooltipRef, "data-visible": visible, "data-position": position, role: "tooltip" }, r, { children: [children, closable && (jsx("button", Object.assign({ onClick: closeTooltip }, { children: jsx("svg", Object.assign({ width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, { children: jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M10.0001 8.82266L13.5359 5.28682C13.8613 4.96143 14.3889 4.96143 14.7143 5.28682C15.0397 5.61221 15.0397 6.13977 14.7143 6.46516L11.1784 10.001L14.7143 13.5368C15.0397 13.8622 15.0397 14.3898 14.7143 14.7152C14.3889 15.0405 13.8613 15.0405 13.5359 14.7152L10.0001 11.1793L6.46428 14.7152C6.13889 15.0405 5.61133 15.0405 5.28594 14.7152C4.96056 14.3898 4.96056 13.8622 5.28594 13.5368L8.82178 10.001L5.28594 6.46516C4.96056 6.13977 4.96056 5.61221 5.28594 5.28682C5.61133 4.96143 6.13889 4.96143 6.46428 5.28682L10.0001 8.82266Z", fill: "#F2F2F2" }) })) })))] })));
};

const Button = (_a) => {
    var { className, drawerId, hasNotifications, onClick, tooltipText, tooltipPosition = 'bottom', showTooltip } = _a, r = __rest(_a, ["className", "drawerId", "hasNotifications", "onClick", "tooltipText", "tooltipPosition", "showTooltip"]);
    const classes = classNames('notification-drawer-button', className, {
        'pulse': hasNotifications
    });
    const handleClick = () => {
        toggleDrawer({ id: drawerId, show: true });
        onClick === null || onClick === void 0 ? void 0 : onClick();
    };
    return (jsxs(Button$1, Object.assign({ iconOnly: true, className: classes, onClick: handleClick }, r, { children: [jsx(Icon, { children: jsx("svg", Object.assign({ width: 24, height: 24, xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 24 24" }, { children: jsx("path", { fill: "#333333", d: "M20 17h2v2H2v-2h2v-7a8 8 0 1 1 16 0v7Zm-2 0v-7a6 6 0 1 0-12 0v7h12Zm-9 4h6v2H9v-2Z" }) })) }), jsx(Tooltip, Object.assign({ visible: showTooltip, position: tooltipPosition }, { children: jsx(Text, Object.assign({ as: 'p' }, { children: tooltipText })) }))] })));
};

function Notificationdrawer(_a) {
    var { className, handleClose, id, position = 'left' } = _a, r = __rest(_a, ["className", "handleClose", "id", "position"]);
    const drawerRef = useRef(null);
    const classes = classNames('notification-drawer', className, {
        [`${position}`]: position,
    });
    const closeDrawer = () => {
        if (!drawerRef.current || drawerRef.current.dataset['visible'] !== 'true')
            return;
        toggleDrawer({ id });
        handleClose === null || handleClose === void 0 ? void 0 : handleClose();
    };
    useOnClickOutside(drawerRef, closeDrawer);
    return (jsx("div", Object.assign({ className: "notification-drawer-overlay hidden", "data-id": `overlay-${id}` }, { children: jsx("div", Object.assign({ ref: drawerRef, className: classes, "data-id": id, "data-position": position, "data-visible": "false" }, r)) })));
}
Notificationdrawer.Header = Header;
Notificationdrawer.Card = Card;
Notificationdrawer.Body = Body;
Notificationdrawer.Button = Button;

export { Notificationdrawer };
